import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  FiBarChart2 as BarChart3,
  FiSearch as Search,
  FiFileText as FileText,
  FiTarget as Target,
  FiShield as Shield,
  FiHelpCircle as HelpCircle,
  FiInfo as Info,
  FiPlay as Play,
  FiTool as Wrench,
  FiActivity as Activity,
  FiAlertCircle as AlertCircle,
  FiKey as Key,
  FiCpu as Cpu,
  FiCode as Code,
} from 'react-icons/fi';
import GettingStartedBanner from './GettingStartedBanner';
import { useOnboardingStatus } from '../../../hooks/useOnboardingStatus';
import { demoModeManager } from '../../../services/demoModeManager';
import HelpPanel from '../../../components/common/HelpPanel';
import AIOperationsHelp from '../../../features/ai-operations/help';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const AIOperationsLayout = () => {
  const location = useLocation();
  const { isComplete: onboardingComplete, hasApiKey, hasPrompt, refresh: refreshOnboarding } = useOnboardingStatus();
  const agentOpsOnboardingComplete = hasApiKey && hasPrompt;
  const allOnboardingComplete = onboardingComplete && agentOpsOnboardingComplete;

  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem('ai_operations_onboarding_dismissed') !== 'true';
  });

  useEffect(() => {
    refreshOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (allOnboardingComplete) {
      localStorage.setItem('ai_operations_onboarding_dismissed', 'true');
      setShowOnboarding(false);
    }
  }, [allOnboardingComplete]);

  const handleDismissOnboarding = () => {
    localStorage.setItem('ai_operations_onboarding_dismissed', 'true');
    setShowOnboarding(false);
  };

  const handleShowOnboarding = () => {
    localStorage.removeItem('ai_operations_onboarding_dismissed');
    setShowOnboarding(true);
  };

  const [showHelpPanel, setShowHelpPanel] = useState(false);

  const getActiveContext = (): { tab: string; subTab: string | null } => {
    const searchParams = new URLSearchParams(location.search);
    const subTab = searchParams.get('tab');
    const path = location.pathname;

    // GenAIOps sections
    if (path.includes('/observability')) return { tab: 'observability', subTab: subTab || 'traces' };
    if (path.includes('/registry')) return { tab: 'registry', subTab: subTab || 'prompts' };
    if (path.includes('/evaluation')) return { tab: 'evaluation', subTab: subTab || 'overview' };
    if (path.includes('/governance')) return { tab: 'governance', subTab: subTab || 'approvals' };
    // AgentOps sections
    if (path.includes('/runs')) return { tab: 'runs', subTab: null };
    if (path.includes('/tools')) return { tab: 'tools', subTab: null };
    if (path.includes('/apm')) return { tab: 'apm', subTab: subTab || 'overview' };
    if (path.includes('/incidents')) return { tab: 'incidents', subTab: subTab || 'safety' };
    // Settings sections moved here
    if (path.includes('/api-keys')) return { tab: 'api-keys', subTab: null };
    if (path.includes('/llm-providers')) return { tab: 'llm-providers', subTab: null };
    if (path.includes('/sdk')) return { tab: 'sdk', subTab: null };
    return { tab: 'overview', subTab: null };
  };

  const mainItems: NavItem[] = [
    { path: '/ai-operations/overview', label: 'Overview', icon: BarChart3 },
    { path: '/ai-operations/sdk', label: 'SDK', icon: Code },
  ];

  const navGroups: { label: string; items: NavItem[] }[] = [
    {
      label: 'GENAI',
      items: [
        { path: '/ai-operations/observability', label: 'Observability', icon: Search },
        { path: '/ai-operations/registry', label: 'Registry', icon: FileText },
        { path: '/ai-operations/evaluation', label: 'Evaluation', icon: Target },
        { path: '/ai-operations/governance', label: 'Governance', icon: Shield },
      ],
    },
    {
      label: 'AGENTS',
      items: [
        { path: '/ai-operations/runs', label: 'Runs', icon: Play },
        { path: '/ai-operations/tools', label: 'Tools', icon: Wrench },
        { path: '/ai-operations/apm', label: 'APM', icon: Activity },
        { path: '/ai-operations/incidents', label: 'Incidents', icon: AlertCircle },
      ],
    },
    {
      label: 'CONFIG',
      items: [
        { path: '/ai-operations/api-keys', label: 'API Keys', icon: Key },
        { path: '/ai-operations/llm-providers', label: 'LLM Providers', icon: Cpu },
      ],
    },
  ];

  const isActive = (path: string): boolean => {
    if (path === '/ai-operations/overview') {
      return location.pathname === path || location.pathname === '/ai-operations';
    }
    return location.pathname.startsWith(path);
  };

  const docsBaseUrl = 'https://docs.lens.aivilo.ai';
  const getDocsUrl = (): string => {
    const ctx = getActiveContext();
    const tabToDocsPath: Record<string, string> = {
      'overview': '/ai-operations',
      'observability': '/ai-operations/traces',
      'registry': '/ai-operations/prompts',
      'evaluation': '/ai-operations/evaluations',
      'governance': '/ai-operations/governance',
      'runs': '/ai-operations/runs',
      'tools': '/ai-operations/tools',
      'apm': '/ai-operations/apm',
      'incidents': '/ai-operations/incidents',
      'api-keys': '/ai-operations/api-keys',
      'llm-providers': '/ai-operations/llm-providers',
      'sdk': '/ai-operations/sdk',
    };
    return `${docsBaseUrl}${tabToDocsPath[ctx.tab] || '/ai-operations'}`;
  };

  return (
    <div>
      {/* AI Operations Sub-Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <nav className="flex items-end overflow-x-auto nav-scroll-container" aria-label="AI Operations navigation">
              {/* Main group */}
              <div className="relative flex items-end px-1">
                <span className="absolute top-0 left-2 text-[9px] font-semibold tracking-widest text-gray-400 dark:text-gray-500 uppercase select-none">
                  MAIN
                </span>
                <div className="flex space-x-4">
                  {mainItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`${
                          isActive(item.path)
                            ? 'border-blue-500 text-gray-900 dark:text-white'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors flex-shrink-0`}
                      >
                        <Icon className="w-4 h-4 mr-1.5" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {navGroups.map((group) => (
                <React.Fragment key={group.label}>
                  {/* Separator */}
                  <div className="self-stretch flex items-center mx-3">
                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-600" />
                  </div>

                  <div className="relative flex items-end px-1">
                    {/* Group label - absolute so it doesn't add height */}
                    <span className="absolute top-0 left-2 text-[9px] font-semibold tracking-widest text-gray-400 dark:text-gray-500 uppercase select-none">
                      {group.label}
                    </span>
                    {/* Group items */}
                    <div className="flex space-x-4">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`${
                              isActive(item.path)
                                ? 'border-blue-500 text-gray-900 dark:text-white'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors flex-shrink-0`}
                          >
                            <Icon className="w-4 h-4 mr-1.5" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </nav>
            {/* Action buttons */}
            <div className="flex items-center gap-3 flex-shrink-0 ml-3">
              {/* Getting Started banner toggle */}
              {!showOnboarding && !allOnboardingComplete && (
                <button
                  onClick={handleShowOnboarding}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                  title="Getting Started"
                  aria-label="Getting Started"
                >
                  <Info className="w-5 h-5" />
                </button>
              )}
              {/* Help panel toggle */}
              <button
                onClick={() => setShowHelpPanel(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Help"
                aria-label="Help"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Banner - hidden in demo mode */}
      {showOnboarding && !demoModeManager.getDemoMode().isDemoMode && (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <GettingStartedBanner onDismiss={handleDismissOnboarding} />
        </div>
      )}

      {/* AI Operations Content */}
      <Outlet />

      {/* Help Panel */}
      <HelpPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        title="Help"
      >
        <AIOperationsHelp activeTab={getActiveContext().tab} activeSubTab={getActiveContext().subTab ?? undefined} />
        {/* Docs link */}
        <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Full documentation available at{' '}
            <a
              href={getDocsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              docs.lens.aivilo.ai
            </a>
          </p>
        </div>
      </HelpPanel>
    </div>
  );
};

export default AIOperationsLayout;
